import { z } from "zod";
import { communityPublicIdentitySchema } from "./community-genesis-v1";
import { referralCodeSchema } from "./community-v1";
import { WIRE_OFFSET_DATETIME_PATTERN } from "./wire-patterns";

export const MEME_PAGE_SIZE = 24;
export const MEME_UPLOAD_MAX_BYTES = 8 * 1024 * 1024;
export const memePublicIdSchema = z.string().regex(/^[23456789abcdefghjkmnpqrstuvwxyz]{10}$/);
export const memeTagSchema = z.string().trim().toLowerCase().regex(/^[a-z0-9][a-z0-9-]{0,23}$/);
const tags = (max: number) => z.array(memeTagSchema).max(max).transform((values) => [...new Set(values)].sort());
/** Optional upload text: surrounding whitespace is dropped and blank text counts as absent. */
const optionalText = (max: number) => z.preprocess(
  (value) => typeof value === "string" && value.trim() === "" ? undefined : value,
  z.string().trim().max(max).optional(),
);
/** Only the image is required; title, alt text and tags are all optional. */
export const memeUploadMetaSchema = z.object({
  title: optionalText(80),
  altText: optionalText(300),
  tags: tags(8).default([]),
}).strict();
export const memeQuerySchema = z.object({
  q: z.string().trim().min(1).max(100).optional(),
  tags: z.preprocess((value) => typeof value === "string" ? value.split(",") : value, tags(3).optional()),
  creator: referralCodeSchema.optional(),
  kind: z.enum(["static", "animated"]).optional(),
  period: z.enum(["24h", "7d", "30d", "all"]).default("all"),
  sort: z.enum(["new", "old", "shared", "az", "relevant"]).optional(),
  cursor: z.string().min(1).max(2048).optional(),
}).strict().superRefine((value, context) => {
  if (value.sort === "relevant" && !value.q) context.addIssue({ code: "custom", path: ["sort"], message: "Relevance requires a search" });
}).transform((value) => ({ ...value, sort: value.sort ?? (value.q ? "relevant" as const : "new" as const) }));
export const memeMediaSchema = z.object({
  full: z.string().url(), thumb: z.string().url(), poster: z.string().url(), card: z.string().url(),
}).strict();
export const memeSchema = z.object({
  publicId: memePublicIdSchema,
  title: z.string().min(1).max(80).nullable(),
  altText: z.string().min(1).max(300).nullable(),
  tags: tags(8),
  format: z.enum(["png", "jpeg", "gif", "webp"]),
  animated: z.boolean(),
  width: z.number().int().positive().max(2048),
  height: z.number().int().positive().max(2048),
  createdAt: z.stringFormat("date-time", WIRE_OFFSET_DATETIME_PATTERN),
  shareCount: z.string().regex(/^(0|[1-9][0-9]*)$/),
  media: memeMediaSchema,
  creator: z.object({ code: referralCodeSchema, identity: communityPublicIdentitySchema }).strict(),
}).strict();
export const memesResponseSchema = z.object({ memes: z.array(memeSchema).max(MEME_PAGE_SIZE), nextCursor: z.string().nullable() }).strict();
export const memePermissionSchema = z.object({ canUpload: z.boolean() }).strict();
export const memeTagsResponseSchema = z.object({ tags: z.array(z.object({ tag: memeTagSchema, count: z.number().int().nonnegative() }).strict()) }).strict();
export const memeShareRequestSchema = z.object({ channel: z.enum(["x", "copy"]) }).strict();
export const memeCursorQuerySchema = z.object({ cursor: z.string().min(1).max(2048).optional() }).strict();
/** An existing live upload is linked only when it belongs to the requesting member. */
export const memeDuplicateResponseSchema = z.object({ error: z.literal("duplicate_meme"), publicId: memePublicIdSchema.optional() }).strict();
export type Meme = z.infer<typeof memeSchema>;
export type MemeQuery = z.infer<typeof memeQuerySchema>;
export type MemeUploadMeta = z.infer<typeof memeUploadMetaSchema>;
